import axios from 'axios';
import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { PieChart, Plus, Trash2, TrendingUp, TrendingDown, DollarSign, Target, Edit } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = (typeof window !== 'undefined' ? window.__API_URL__ : process.env.REACT_APP_BACKEND_URL) || '';

const TYPE_PLACEMENT = [
  { value: 'reer', label: 'REER', color: 'bg-blue-100 text-blue-700' },
  { value: 'celi', label: 'CELI', color: 'bg-green-100 text-green-700' },
  { value: 'actions', label: 'Actions', color: 'bg-purple-100 text-purple-700' },
  { value: 'immobilier', label: 'Immobilier', color: 'bg-amber-100 text-amber-700' },
  { value: 'epargne', label: 'Épargne', color: 'bg-teal-100 text-teal-700' },
  { value: 'crypto', label: 'Crypto', color: 'bg-orange-100 text-orange-700' },
  { value: 'autre', label: 'Autre', color: 'bg-gray-100 text-gray-700' },
];

const getType = (v) => TYPE_PLACEMENT.find(t => t.value === v) || TYPE_PLACEMENT[TYPE_PLACEMENT.length - 1];
const fmt = (n) => Number(n || 0).toLocaleString('fr-CA', { style: 'currency', currency: 'CAD' });

export function Placements() {
  const [placements, setPlacements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState({ nom: '', type: 'reer', montant_investi: '', valeur_actuelle: '', institution: '', date_debut: '', notes: '' });

  useEffect(() => { loadPlacements(); }, []);

  const loadPlacements = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/valorisation/placements`);
      if (res.status === 200) { const d = res.data; setPlacements(d.placements || []); }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const openAdd = () => {
    setEditItem(null);
    setForm({ nom: '', type: 'reer', montant_investi: '', valeur_actuelle: '', institution: '', date_debut: '', notes: '' });
    setShowDialog(true);
  };

  const openEdit = (p) => {
    setEditItem(p);
    setForm({ nom: p.nom, type: p.type, montant_investi: p.montant_investi?.toString() || '', valeur_actuelle: p.valeur_actuelle?.toString() || '', institution: p.institution || '', date_debut: p.date_debut || '', notes: p.notes || '' });
    setShowDialog(true);
  };

  const save = async () => {
    if (!form.nom || !form.montant_investi) { toast.error('Nom et montant investi requis'); return; }
    try {
      const payload = { ...form, montant_investi: parseFloat(form.montant_investi) || 0, valeur_actuelle: parseFloat(form.valeur_actuelle) || parseFloat(form.montant_investi) || 0 };
      const res = editItem
        ? await axios.put(`${API_URL}/api/valorisation/placements/${editItem.id}`, payload)
        : await axios.post(`${API_URL}/api/valorisation/placements`, payload);
      if (res.status !== 200) throw new Error();
      toast.success(editItem ? 'Placement mis à jour!' : 'Placement ajouté!');
      setShowDialog(false);
      loadPlacements();
    } catch { toast.error('Erreur lors de la sauvegarde'); }
  };

  const deleteItem = async (id) => {
    if (!window.confirm('Supprimer ce placement ?')) return;
    try {
      await axios.delete(`${API_URL}/api/valorisation/placements/${id}`);
      loadPlacements();
      toast.success('Supprimé');
    } catch { toast.error('Erreur'); }
  };

  const totalInvesti = placements.reduce((s, p) => s + (p.montant_investi || 0), 0);
  const totalActuel = placements.reduce((s, p) => s + (p.valeur_actuelle || 0), 0);
  const rendement = totalInvesti > 0 ? ((totalActuel - totalInvesti) / totalInvesti * 100) : 0;

  // Group by type
  const byType = placements.reduce((acc, p) => {
    acc[p.type] = (acc[p.type] || 0) + (p.valeur_actuelle || 0);
    return acc;
  }, {});

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <PieChart className="w-7 h-7 text-blue-600" /> Placements
          </h1>
          <p className="text-sm text-gray-500">Suivi des investissements et placements financiers</p>
        </div>
        <Button onClick={openAdd} className="bg-blue-600 hover:bg-blue-700" data-testid="add-placement-btn">
          <Plus className="w-4 h-4 mr-2" /> Ajouter
        </Button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 mb-1">
              <DollarSign className="w-5 h-5 text-blue-600" />
              <span className="text-sm font-medium text-blue-600">Total investi</span>
            </div>
            <p className="text-2xl font-bold text-blue-700">{fmt(totalInvesti)}</p>
          </CardContent>
        </Card>
        <Card className={totalActuel >= totalInvesti ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 mb-1">
              {totalActuel >= totalInvesti ? <TrendingUp className="w-5 h-5 text-green-600" /> : <TrendingDown className="w-5 h-5 text-red-600" />}
              <span className={`text-sm font-medium ${totalActuel >= totalInvesti ? 'text-green-600' : 'text-red-600'}`}>Valeur actuelle</span>
            </div>
            <p className={`text-2xl font-bold ${totalActuel >= totalInvesti ? 'text-green-700' : 'text-red-700'}`}>{fmt(totalActuel)}</p>
          </CardContent>
        </Card>
        <Card className={rendement >= 0 ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 mb-1">
              <Target className="w-5 h-5 text-emerald-600" />
              <span className="text-sm font-medium text-emerald-600">Rendement total</span>
            </div>
            <p className={`text-2xl font-bold ${rendement >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
              {rendement >= 0 ? '+' : ''}{rendement.toFixed(2)}%
            </p>
            <p className="text-sm text-gray-500">{rendement >= 0 ? '+' : ''}{fmt(totalActuel - totalInvesti)}</p>
          </CardContent>
        </Card>
      </div>

      {/* Distribution by type */}
      {Object.keys(byType).length > 0 && (
        <Card>
          <CardHeader><CardTitle className="text-base">Répartition par catégorie</CardTitle></CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-3">
              {Object.entries(byType).map(([type, val]) => {
                const t = getType(type);
                const pct = totalActuel > 0 ? (val / totalActuel * 100) : 0;
                return (
                  <div key={type} className={`px-4 py-2 rounded-lg ${t.color}`}>
                    <p className="text-xs font-medium">{t.label}</p>
                    <p className="text-sm font-bold">{fmt(val)}</p>
                    <p className="text-xs">{pct.toFixed(1)}%</p>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* List */}
      {loading ? (
        <div className="text-center py-10 text-gray-400">Chargement...</div>
      ) : placements.length === 0 ? (
        <Card>
          <CardContent className="text-center py-12">
            <PieChart className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p className="text-gray-500">Aucun placement enregistré.</p>
            <p className="text-sm text-gray-400 mt-1">Cliquez sur "Ajouter" pour commencer à suivre vos investissements.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {placements.map(p => {
            const t = getType(p.type);
            const diff = (p.valeur_actuelle || 0) - (p.montant_investi || 0);
            const pct = p.montant_investi > 0 ? (diff / p.montant_investi * 100) : 0;
            return (
              <Card key={p.id} className="hover:shadow-md transition-shadow" data-testid={`placement-${p.id}`}>
                <CardContent className="py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center font-bold ${t.color}`}>
                        {p.nom?.[0]?.toUpperCase() || '?'}
                      </div>
                      <div>
                        <p className="font-semibold">{p.nom}</p>
                        <div className="flex items-center gap-2 mt-0.5">
                          <Badge className={`${t.color} text-xs`}>{t.label}</Badge>
                          {p.institution && <span className="text-xs text-gray-400">{p.institution}</span>}
                          {p.date_debut && <span className="text-xs text-gray-400">Depuis {p.date_debut}</span>}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right hidden sm:block">
                        <p className="text-xs text-gray-400">Investi</p>
                        <p className="font-semibold text-sm">{fmt(p.montant_investi)}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-gray-400">Valeur actuelle</p>
                        <p className="font-bold text-base">{fmt(p.valeur_actuelle)}</p>
                        <p className={`text-xs font-medium ${diff >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {diff >= 0 ? '+' : ''}{fmt(diff)} ({pct >= 0 ? '+' : ''}{pct.toFixed(1)}%)
                        </p>
                      </div>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => openEdit(p)}>
                          <Edit className="w-3.5 h-3.5 text-gray-400" />
                        </Button>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => deleteItem(p.id)}>
                          <Trash2 className="w-3.5 h-3.5 text-red-400" />
                        </Button>
                      </div>
                    </div>
                  </div>
                  {p.notes && <p className="text-xs text-gray-400 mt-2 ml-13">{p.notes}</p>}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editItem ? 'Modifier le placement' : 'Ajouter un placement'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Nom du placement *</Label>
                <Input value={form.nom} onChange={(e) => setForm(f => ({ ...f, nom: e.target.value }))} placeholder="Ex: REER TD, Actions Apple..." data-testid="placement-nom" />
              </div>
              <div>
                <Label>Type</Label>
                <Select value={form.type} onValueChange={(v) => setForm(f => ({ ...f, type: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{TYPE_PLACEMENT.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Montant investi ($) *</Label>
                <Input type="number" step="0.01" min="0" value={form.montant_investi} onChange={(e) => setForm(f => ({ ...f, montant_investi: e.target.value }))} placeholder="0.00" data-testid="placement-montant-investi" />
              </div>
              <div>
                <Label>Valeur actuelle ($)</Label>
                <Input type="number" step="0.01" min="0" value={form.valeur_actuelle} onChange={(e) => setForm(f => ({ ...f, valeur_actuelle: e.target.value }))} placeholder="0.00" data-testid="placement-valeur-actuelle" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Institution financière</Label>
                <Input value={form.institution} onChange={(e) => setForm(f => ({ ...f, institution: e.target.value }))} placeholder="Banque, courtier..." />
              </div>
              <div>
                <Label>Date de début</Label>
                <Input type="date" value={form.date_debut} onChange={(e) => setForm(f => ({ ...f, date_debut: e.target.value }))} />
              </div>
            </div>
            <div>
              <Label>Notes</Label>
              <Input value={form.notes} onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Informations complémentaires..." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Annuler</Button>
            <Button onClick={save} data-testid="save-placement-btn">{editItem ? 'Mettre à jour' : 'Ajouter'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
